import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Button } from 'c1ui'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1LabeledTextFieldWithPresets } from 'components/core/RHKFormComponents/C1LabeledTextFieldWithPresets'
import { useAlert } from 'hooks/useAlert'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { UIMessageFeedback_Sentiment, UIMessagesServiceCreateFeedbackRequest } from 'pbts/c1/api/conversations/v1/conversations'
import { FC, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { createMessageFeedbackApi } from 'redux_toolkit/conversations'

type MessageFeedbackProps = {
    conversationId: string
    messageId: string
}

export const MessageFeedback: FC<MessageFeedbackProps> = ({ conversationId, messageId }) => {
    const intl = useIntl()
    const [sentiment, setSentiment] = useState<UIMessageFeedback_Sentiment | null>(null)
    const [feedbackText, setFeedbackText] = useState<string>('')
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
    const toast = useAlert()

    const {
        mutate: createFeedback,
        inProgress: isSubmitting,
    } = createMessageFeedbackApi()

    const handleThumbsClick = (type: UIMessageFeedback_Sentiment): void => {
        if (sentiment === type) {
            setSentiment(null)
        } else {
            setSentiment(type)
        }
    }

    const handleClose = (): void => {
        setFeedbackText('')
        setSentiment(null)
    }

    const handleSubmit = async (): Promise<void> => {
        if (sentiment === null) return

        try {
            // Submit complete feedback with sentiment and text
            await createFeedback(UIMessagesServiceCreateFeedbackRequest.create({
                feedback: {
                    conversationId,
                    messageId,
                    sentiment,
                    text: feedbackText,
                },
            }))
        } catch (err) {
            toast(
                intl.formatMessage(
                    {
                        id: 'chat.feedback.error',
                        defaultMessage: 'Failed to submit feedback. Please try again. {code}: {msg}',
                    },
                    {
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    },
                ),
                'error',
            )
            return
        }
        setIsSubmitted(true)
    }

    return <Box sx={{ mt: 1 }}>
        <Stack direction="row" spacing={1}>
            <Tooltip
                title={intl.formatMessage({
                    id: 'chat.feedback.thumbs_up',
                    defaultMessage: 'This response was helpful',
                })}
                placement="top"
                disableHoverListener={isSubmitted}
            >
                <IconButton
                    size="small"
                    onClick={(): void => handleThumbsClick(UIMessageFeedback_Sentiment.POSITIVE)}
                    aria-label={intl.formatMessage({
                        id: 'chat.feedback.thumbs_up',
                        defaultMessage: 'This response was helpful',
                    })}
                    aria-pressed={sentiment === UIMessageFeedback_Sentiment.POSITIVE}
                    disabled={isSubmitted}
                >
                    <C1Icon icon={ThumbsUp} size="sm" />
                </IconButton>
            </Tooltip>
            <Tooltip
                title={intl.formatMessage({
                    id: 'chat.feedback.thumbs_down',
                    defaultMessage: 'This response was not helpful',
                })}
                placement="top"
                disableHoverListener={isSubmitted}
            >
                <IconButton
                    size="small"
                    onClick={(): void => handleThumbsClick(UIMessageFeedback_Sentiment.NEGATIVE)}
                    aria-label={intl.formatMessage({
                        id: 'chat.feedback.thumbs_down',
                        defaultMessage: 'This response was not helpful',
                    })}
                    aria-pressed={sentiment === UIMessageFeedback_Sentiment.NEGATIVE}
                    disabled={isSubmitted}
                >
                    <C1Icon icon={ThumbsDown} size="sm" />
                </IconButton>
            </Tooltip>
        </Stack>

        {sentiment !== null &&
            <Box sx={{ mt: 2 }}>
                {isSubmitted
                    ? <Typography color="text.secondary" variant="unconfigured">
                        <FormattedMessage
                            id="chat.feedback.thanks"
                            defaultMessage="Thank you for your feedback. Your response has been recorded."
                        />
                    </Typography>
                    : <Box sx={{ py: 2, px: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <C1LabeledTextFieldWithPresets
                            name="feedback"
                            label={sentiment === UIMessageFeedback_Sentiment.POSITIVE
                                ? intl.formatMessage({
                                    id: 'chat.feedback.label_positive',
                                    defaultMessage: 'What did you like about this interaction? (optional)',
                                })
                                : intl.formatMessage({
                                    id: 'chat.feedback.label_negative',
                                    defaultMessage: 'What could be improved? (optional)',
                                })}
                            placeholder={intl.formatMessage({
                                id: 'chat.feedback.placeholder',
                                defaultMessage: 'Share your thoughts...',
                            })}
                            value={feedbackText}
                            onChange={(e): void => setFeedbackText(e.target.value)}
                            presets={sentiment === UIMessageFeedback_Sentiment.POSITIVE
                                ? [
                                    intl.formatMessage({ id: 'chat.feedback.preset.helpful_accurate', defaultMessage: 'Helpful and accurate' }),
                                    intl.formatMessage({ id: 'chat.feedback.preset.clear_explanation', defaultMessage: 'Clear explanation' }),
                                    intl.formatMessage({ id: 'chat.feedback.preset.solved_problem', defaultMessage: 'Solved my problem' }),
                                    intl.formatMessage({ id: 'chat.feedback.preset.good_suggestions', defaultMessage: 'Good suggestions' }),
                                ]
                                : sentiment === UIMessageFeedback_Sentiment.NEGATIVE
                                    ? [
                                        intl.formatMessage({ id: 'chat.feedback.preset.inaccurate_info', defaultMessage: 'Inaccurate information' }),
                                        intl.formatMessage({ id: 'chat.feedback.preset.not_helpful', defaultMessage: 'Not helpful' }),
                                        intl.formatMessage({ id: 'chat.feedback.preset.confusing_response', defaultMessage: 'Confusing response' }),
                                        intl.formatMessage({ id: 'chat.feedback.preset.missing_details', defaultMessage: 'Missing important details' }),
                                    ]
                                    : []}
                            setValue={setFeedbackText}
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={4}
                        />

                        <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
                            <C1Button
                                variant="outlined"
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                <FormattedMessage id="chat.feedback.cancel" defaultMessage="Dismiss" />
                            </C1Button>
                            <C1Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={isSubmitting || sentiment === null}
                            >
                                <FormattedMessage id="chat.feedback.submit" defaultMessage="Submit" />
                            </C1Button>
                        </Stack>
                    </Box>
                }
            </Box>
        }
    </Box>
}
